"use strict";
/**
 * Order Button Registry
 *
 * Single source of truth for every logical order button across the
 * order-page layouts (traditional / baseball / basketball / soccer): its stable ID,
 * human-readable label, owning layout, kind, and the physical DOM buttons
 * that back it.
 *
 * Consumers:
 * - additional-markets-service re-exports the ID types and labels
 * - additional-markets badge/tooltip UI maps IDs to DOM elements
 * - the additional-markets modal renders its button-selection grid from
 *   the active layout's descriptors
 *
 * Pure module-scope const data — safe to import from services, handlers,
 * and UI modules alike (its only import is a type, erased at compile time).
 *
 * @module services/order-button-registry
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ORDER_BUTTON_DOM_IDS = exports.ORDER_BUTTON_LABELS = exports.ORDER_BUTTONS = void 0;
exports.getButtonsForLayout = getButtonsForLayout;
exports.getButtonDescriptor = getButtonDescriptor;
exports.getOrderButtonTone = getOrderButtonTone;
// ---------------------------------------------------------------------------
// Registry data
// ---------------------------------------------------------------------------
/** Both-side bindings following the -a = yes / -b = no column convention. */
function pair(base) {
    return [
        { elementId: `${base}-a`, side: "yes" },
        { elementId: `${base}-b`, side: "no" },
    ];
}
/** One physical binding for a single-side descriptor (basketball, soccer, traditional). */
function single(elementId, side) {
    return [{ elementId, side }];
}
/** Every logical order button, grouped by layout in display order. */
exports.ORDER_BUTTONS = [
    // Traditional
    {
        id: "trad_buy_yes",
        label: "Buy YES (A)",
        layout: "traditional",
        kind: "buy",
        group: "traditional",
        side: "yes",
        bindings: single("buy-option-a", "yes"),
    },
    {
        id: "trad_buy_no",
        label: "Buy NO (B)",
        layout: "traditional",
        kind: "buy",
        group: "traditional",
        side: "no",
        bindings: single("buy-option-b", "no"),
    },
    // Baseball offense
    { id: "misc_zero_off", label: "MISC 0% (O)", layout: "baseball", kind: "buy", group: "offense", bindings: pair("bb-misc-top") },
    { id: "misc_scaled_off", label: "MISC Scaled (O)", layout: "baseball", kind: "buy", group: "offense", bindings: pair("bb-misc-bottom") },
    { id: "ext_off", label: "EXT (O)", layout: "baseball", kind: "additional-only", group: "offense", bindings: pair("bb-ext-off") },
    { id: "single", label: "Single", layout: "baseball", kind: "buy", group: "offense", bindings: pair("bb-single") },
    { id: "double", label: "DBL", layout: "baseball", kind: "buy", group: "offense", bindings: pair("bb-dbl") },
    { id: "triple", label: "TRPL", layout: "baseball", kind: "buy", group: "offense", bindings: pair("bb-trpl") },
    { id: "hr", label: "HR", layout: "baseball", kind: "buy", group: "offense", bindings: pair("bb-hr") },
    // Baseball defense
    { id: "misc_zero_def", label: "MISC 0% (D)", layout: "baseball", kind: "buy", group: "defense", bindings: pair("bb-misc-def-top") },
    { id: "misc_scaled_def", label: "MISC Scaled (D)", layout: "baseball", kind: "buy", group: "defense", bindings: pair("bb-misc-def-bottom") },
    { id: "ext_def", label: "EXT (D)", layout: "baseball", kind: "additional-only", group: "defense", bindings: pair("bb-ext-def") },
    { id: "strikeout", label: "K", layout: "baseball", kind: "buy", group: "defense", bindings: pair("bb-ko") },
    { id: "out", label: "Out", layout: "baseball", kind: "buy", group: "defense", bindings: pair("bb-out") },
    { id: "double_play", label: "DP", layout: "baseball", kind: "buy", group: "defense", bindings: pair("bb-dp") },
    { id: "triple_play", label: "TP", layout: "baseball", kind: "buy", group: "defense", bindings: pair("bb-tp") },
    // Basketball — split per side so an assignment can target one button
    { id: "bk_misc_yes", label: "MISC (BK) – Yes", layout: "basketball", kind: "buy", group: "basketball", side: "yes", bindings: single("bk-misc-a", "yes") },
    { id: "bk_misc_no", label: "MISC (BK) – No", layout: "basketball", kind: "buy", group: "basketball", side: "no", bindings: single("bk-misc-b", "no") },
    { id: "bk_ext_yes", label: "EXT (BK) – Yes", layout: "basketball", kind: "additional-only", group: "basketball", side: "yes", bindings: single("bk-ext-a", "yes") },
    { id: "bk_ext_no", label: "EXT (BK) – No", layout: "basketball", kind: "additional-only", group: "basketball", side: "no", bindings: single("bk-ext-b", "no") },
    { id: "bk_2ptr_yes", label: "2PTR – Yes", layout: "basketball", kind: "buy", group: "basketball", side: "yes", bindings: single("bk-2ptr-a", "yes") },
    { id: "bk_2ptr_no", label: "2PTR – No", layout: "basketball", kind: "buy", group: "basketball", side: "no", bindings: single("bk-2ptr-b", "no") },
    { id: "bk_3ptr_yes", label: "3PTR – Yes", layout: "basketball", kind: "buy", group: "basketball", side: "yes", bindings: single("bk-3ptr-a", "yes") },
    { id: "bk_3ptr_no", label: "3PTR – No", layout: "basketball", kind: "buy", group: "basketball", side: "no", bindings: single("bk-3ptr-b", "no") },
    // Soccer — split per side like basketball's
    { id: "sc_misc_yes", label: "MISC (SC) – Yes", layout: "soccer", kind: "buy", group: "soccer", side: "yes", bindings: single("sc-misc-a", "yes") },
    { id: "sc_misc_no", label: "MISC (SC) – No", layout: "soccer", kind: "buy", group: "soccer", side: "no", bindings: single("sc-misc-b", "no") },
    { id: "sc_ext_yes", label: "EXT (SC) – Yes", layout: "soccer", kind: "additional-only", group: "soccer", side: "yes", bindings: single("sc-ext-a", "yes") },
    { id: "sc_ext_no", label: "EXT (SC) – No", layout: "soccer", kind: "additional-only", group: "soccer", side: "no", bindings: single("sc-ext-b", "no") },
    { id: "sc_goal_maybe_yes", label: "GOAL? – Yes", layout: "soccer", kind: "buy", group: "soccer", side: "yes", bindings: single("sc-goal-maybe-a", "yes") },
    { id: "sc_goal_maybe_no", label: "GOAL? – No", layout: "soccer", kind: "buy", group: "soccer", side: "no", bindings: single("sc-goal-maybe-b", "no") },
    { id: "sc_goal_yes", label: "GOAL – Yes", layout: "soccer", kind: "buy", group: "soccer", side: "yes", bindings: single("sc-goal-a", "yes") },
    { id: "sc_goal_no", label: "GOAL – No", layout: "soccer", kind: "buy", group: "soccer", side: "no", bindings: single("sc-goal-b", "no") },
    { id: "sc_red_yes", label: "RED CARD – Yes", layout: "soccer", kind: "buy", group: "soccer", side: "yes", bindings: single("sc-red-a", "yes") },
    { id: "sc_red_no", label: "RED CARD – No", layout: "soccer", kind: "buy", group: "soccer", side: "no", bindings: single("sc-red-b", "no") },
];
// ---------------------------------------------------------------------------
// Derived lookups (computed once at module load)
// ---------------------------------------------------------------------------
/** Human-readable labels for all button IDs, regardless of layout. */
exports.ORDER_BUTTON_LABELS = Object.fromEntries(exports.ORDER_BUTTONS.map((b) => [b.id, b.label]));
/** DOM element IDs backing each logical button. */
exports.ORDER_BUTTON_DOM_IDS = Object.fromEntries(exports.ORDER_BUTTONS.map((b) => [b.id, b.bindings.map((x) => x.elementId)]));
/** Descriptors belonging to the given layout, in display order. */
function getButtonsForLayout(layout) {
    return exports.ORDER_BUTTONS.filter((b) => b.layout === layout);
}
/** Descriptor for the given button ID, or undefined for unknown IDs. */
function getButtonDescriptor(id) {
    return exports.ORDER_BUTTONS.find((b) => b.id === id);
}
/**
 * Tone for the given button, or `null` for an unknown ID — callers should fall
 * back to a neutral treatment rather than guess a side, since a wrong colour
 * here points the operator at the wrong physical button.
 *
 * `side` wins over `group` whenever both are present: the operator presses one
 * physical button, and that button has one colour.
 *
 * @param id - Logical order-button ID.
 * @returns The button's tone, or `null` if the ID is not registered.
 */
function getOrderButtonTone(id) {
    const descriptor = getButtonDescriptor(id);
    if (!descriptor)
        return null;
    if (descriptor.side)
        return descriptor.side;
    if (descriptor.group === "offense" || descriptor.group === "defense") {
        return descriptor.group;
    }
    return null;
}
